import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';

type TableName = keyof Database['public']['Tables'];

interface UseProfileDeleteOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

export const useProfileDelete = (options?: UseProfileDeleteOptions) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (profileId: string) => {
      try {
        // Delete records from each table in sequence
        // First delete notifications
        const { error: notificationsError } = await supabase
          .from('notifications')
          .delete()
          .eq('recipient_id', profileId);

        if (notificationsError) throw notificationsError;

        // Delete profile engagement
        const { error: engagementError } = await supabase
          .from('profile_engagement')
          .delete()
          .eq('profile_id', profileId);

        if (engagementError) throw engagementError;

        // Delete profile likes
        const { error: likesError } = await supabase
          .from('profile_likes')
          .delete()
          .eq('profile_id', profileId);

        if (likesError) throw likesError;

        // Delete profile views
        const { error: viewsError } = await supabase
          .from('profile_views')
          .delete()
          .eq('profile_id', profileId);

        if (viewsError) throw viewsError;

        // Delete hire requests
        const { error: hireRequestsError } = await supabase
          .from('hire_requests')
          .delete()
          .eq('profile_id', profileId);

        if (hireRequestsError) throw hireRequestsError;

        // Delete about requests
        const { error: aboutRequestsError } = await supabase
          .from('about_requests')
          .delete()
          .eq('profile_id', profileId);

        if (aboutRequestsError) throw aboutRequestsError;

        // Delete experience requests
        const { error: experienceRequestsError } = await supabase
          .from('experience_requests')
          .delete()
          .eq('profile_id', profileId);

        if (experienceRequestsError) throw experienceRequestsError;

        // Delete portfolio requests
        const { error: portfolioRequestsError } = await supabase
          .from('portfolio_requests')
          .delete()
          .eq('profile_id', profileId);

        if (portfolioRequestsError) throw portfolioRequestsError;

        // Delete user roles
        const { error: userRolesError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', profileId);

        if (userRolesError) throw userRolesError;

        // Finally delete the profile
        const { error: profileError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', profileId);

        if (profileError) throw profileError;

        return { success: true, profileId };
      } catch (error) {
        console.error('Error in profile deletion:', error);
        throw error;
      }
    },
    onMutate: (profileId) => {
      setLoadingStates((prev) => ({ ...prev, [profileId]: true }));
    },
    onSuccess: (result) => {
      setLoadingStates((prev) => ({ ...prev, [result.profileId]: false }));
      
      toast({
        title: 'Success',
        description: 'Profile deleted successfully',
      });

      queryClient.invalidateQueries({
        queryKey: ['superadmin-users'],
        refetchType: 'all'
      });

      if (options?.onSuccess) {
        options.onSuccess(result);
      }
    },
    onError: (error: any) => {
      // Extract profileId from the error message if possible
      const profileId = error?.message?.includes('profile_id') ? 
        error.message.split('profile_id')[1]?.split('"')[1] : 
        'unknown';

      setLoadingStates((prev) => ({ ...prev, [profileId]: false }));
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete profile',
        variant: 'destructive',
      });

      if (options?.onError) {
        options.onError(error.message);
      }
    },
  });

  const deleteProfile = (profileId: string) => {
    mutation.mutate(profileId);
  };

  const isLoading = useCallback(
    (profileId: string) => {
      return loadingStates[profileId] || false;
    },
    [loadingStates]
  );

  return {
    deleteProfile,
    isLoading,
    error: mutation.error,
  };
}; 